import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './library.scss';
import ApiServices from '../../services/apiServices';

const normalizeRows = (data) => {
  if (!Array.isArray(data)) return [];
  return data.reduce((acc, row) => {
    if (Array.isArray(row?.movies)) {
      return acc.concat(row.movies);
    }
    return acc;
  }, []);
};

const getThumb = (item) => {
  const source = item?.image || item?.thumbnail || [];
  return Array.isArray(source) && source.length > 0 ? source[0] : '';
};

const uniqByFilm = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.filmid || seen.has(item.filmid)) return false;
    seen.add(item.filmid);
    return true;
  });
};

const isApiSuccess = (response) => Boolean(response && response.status === 200 && response.data && response.data.valid !== false);

const getDataRows = (response) => (isApiSuccess(response) && Array.isArray(response.data?.data) ? response.data.data : []);

const Library = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('continue');
  const [loading, setLoading] = useState(true);
  const [continueItems, setContinueItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [memberShip, setMemberShip] = useState(false);

  const getWatchlistData = async () => {
    try {
      const direct = await ApiServices.callGetAPI('favlist');
      if (isApiSuccess(direct)) return Array.isArray(direct?.data?.data) ? direct.data.data : [];
    } catch (error) {
      // ignore
    }
    return [];
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [latestWebResult, latestTvResult, membershipResult, watchlistData] = await Promise.allSettled([
          ApiServices.callGetAPI('movies/sflist/web'),
          ApiServices.callGetAPI('movies/sflist/tv'),
          ApiServices.callGetAPI('membership/validate'),
          getWatchlistData()
        ]);

        const latestRows = getDataRows(latestWebResult?.value).length
          ? getDataRows(latestWebResult?.value)
          : getDataRows(latestTvResult?.value);
        const continueRow = Array.isArray(latestRows)
          ? latestRows.find(
            (row) =>
              Number(row?.displayOrder || row?.genrerank) === 3 ||
              String(row?.genre || '').toLowerCase().includes('continue')
          )
          : null;
        const continueMovies = continueRow?.movies || [];
        const allMovies = normalizeRows(latestRows);
        const watchedMovies = uniqByFilm(allMovies.filter((item) => Number(item?.watchedpercentage || item?.watchpercentage || 0) > 0));
        const membershipPayload = membershipResult?.status === 'fulfilled' ? membershipResult.value?.data : null;
        const watchlistPayload = watchlistData?.status === 'fulfilled' ? watchlistData.value : [];

        setContinueItems(continueMovies.length ? continueMovies : watchedMovies);
        setHistoryItems(watchedMovies);
        setWatchlistItems(Array.isArray(watchlistPayload) ? watchlistPayload : []);
        setMemberShip(Boolean(membershipPayload?.valid && membershipPayload?.data?.premiumuser === 1));
      } catch (error) {
        setContinueItems([]);
        setHistoryItems([]);
        setWatchlistItems([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const currentItems = useMemo(() => {
    if (activeTab === 'continue') return continueItems;
    if (activeTab === 'history') return historyItems;
    return watchlistItems;
  }, [activeTab, continueItems, historyItems, watchlistItems]);

  const getEpisodeToResume = async (filmid) => {
    try {
      const result = await ApiServices.callGetAPI(`movie/tv/episodepositions/${filmid}`);
      if (result.status === 200 && result.data.valid) {
        return result.data.data?.recentepisode || 'short1';
      }
      return 'short1';
    } catch (error) {
      return 'short1';
    }
  };

  const handleContinue = async (item) => {
    if (!memberShip) {
      navigate('/subscricption');
      return;
    }
    const type = String(item?.type || '').toLowerCase();
    if (type === 'film') {
      navigate(`/watch/movie/${item.filmid}`);
      return;
    }
    const episodeId = await getEpisodeToResume(item.filmid);
    navigate(`/watch/series/${item.filmid}/${episodeId}`);
  };

  return (
    <div className="library-page main-body">
      <div className="library-header">
        <h1>My Library</h1>
        <p>Watch history, continue watching and your personal watchlist.</p>
      </div>

      <div className="library-tabs">
        <button className={activeTab === 'continue' ? 'active' : ''} onClick={() => setActiveTab('continue')}>Continue Watching</button>
        <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>Watch History</button>
        <button className={activeTab === 'watchlist' ? 'active' : ''} onClick={() => setActiveTab('watchlist')}>My Watchlist</button>
      </div>

      {loading ? <p className="library-empty">Loading library...</p> : null}

      {!loading && currentItems.length === 0 ? (
        <p className="library-empty">No content found in this section yet.</p>
      ) : null}

      {!loading && currentItems.length > 0 ? (
        <div className="library-grid">
          {currentItems.map((item) => (
            <div className="library-card" key={`${activeTab}-${item.filmid}`}>
              <div className="library-poster" onClick={() => navigate(`/details/${item.filmid}`)}>
                {getThumb(item) ? <img src={getThumb(item)} alt={item.title || item.filmid} /> : <span className="library-fallback">No image</span>}
                {(item?.watchedpercentage || item?.watchpercentage) ? (
                  <span className="library-progress">
                    <span style={{ width: `${Math.min(100, Number(item.watchedpercentage || item.watchpercentage || 0))}%` }} />
                  </span>
                ) : null}
              </div>
              <h4>{item.title || item.filmid}</h4>
              <div className="library-actions">
                <button onClick={() => navigate(`/details/${item.filmid}`)}>Details</button>
                <button onClick={() => handleContinue(item)}>Continue</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Library;
